/**
 * Central SIMULADA (RNF19) -- NAO E PRODUCAO.
 *
 * Como os numeros nascem:
 *  1. Uma linha por empresa do catalogo. O volume diario vem de um hash
 *     estavel do id (8 a 16 abandonos por dia, mais 2 a 4 quando ha funil de
 *     expirados) e de uma onda por dia da semana. Nada de Math.random: a
 *     mesma janela mostra sempre a mesma tela.
 *  2. A SERIE E A FONTE. Totais da empresa sao a soma dos seus dias e totais
 *     da plataforma sao a soma das empresas: grafico, cartoes, ranking e
 *     tabela batem entre si por construcao. Volume proporcional aos dias da
 *     janela; "desde o inicio" (janela nula) vale 30 dias.
 *  3. Cada empresa vive um CENARIO fixo pela posicao no catalogo (saudavel,
 *     WhatsApp desconectado, integracao muda ha 3 dias) ou pelo status (em
 *     implantacao = sem webhook). O cenario afeta a serie (a integracao muda
 *     zera os ultimos 3 dias; o WhatsApp caido desliga o Tier 2 e derruba a
 *     taxa em 4 pontos) e gera o alerta correspondente.
 *  4. Comissao e `round(valor * feeBps / 10000)` POR DIA, somada depois, como
 *     o backend congela por conversao (ADR-001). Precos vem das ofertas do
 *     catalogo, em centavos.
 *  5. CUSTO tambem nasce por dia, canal a canal, de USO x TARIFA:
 *       - voz: minutos cobrados (2 por ligacao atendida, 1 por nao atendida)
 *         x R$ 1,20/min;
 *       - WhatsApp: pela API oficial da Meta, conversas x R$ 0,35; pela
 *         Evolution, R$ 89,00/mes por instancia rateados por dia -- custo
 *         FIXO, que continua mesmo com a integracao muda (e o que faz a
 *         margem de um dia sem volume ficar negativa, de proposito);
 *       - e-mail: enviados x R$ 2,90/mil;
 *       - infra: R$ 640,00/mes (servidor R$ 380 + banco R$ 260) rateados por
 *         dia e divididos entre as empresas ativas na proporcao do volume
 *         diario esperado de cada uma.
 *     As tarifas sao EXEMPLOS FIXOS (`TARIFAS_DE_EXEMPLO`); as reais vem
 *     das faturas quando o backend existir.
 *  6. A janela ANTERIOR e a mesma simulacao deslocada `dias` para tras, com
 *     as mesmas regras (uma empresa cadastrada no meio da janela anterior
 *     tem menos dias, e a comparacao mostra isso).
 *  7. Alertas e atividade recente nao dependem da janela: sao retrato de
 *     AGORA_SIMULADO. A excecao e "margem negativa", que e retrato do
 *     periodo por definicao.
 */

import { AGORA_SIMULADO, menosMinutos } from '../../lib/api';
import { PROVIDER_LABEL, formatBRLFromCents } from '../../lib/format';
import { EMPRESAS, ofertaCiclica, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import { rangeDays } from '../../lib/period';
import type { CompanyStatus, CompanySummary, PeriodRange, ProductOffer } from '../../types';
import type {
  AlertSeverity,
  ChannelUsage,
  CompanyPrevious,
  CompanyRow,
  CostByChannel,
  CostTariffs,
  PlatformAlert,
  PlatformDailyPoint,
  PlatformEvent,
  PlatformEventKind,
  PlatformOverview,
  PlatformTotals,
  WhatsAppRoute,
} from './types';
import { WHATSAPP_ROUTE_LABEL } from './vocabulario';

const DIA_MS = 86_400_000;
const MINUTOS_POR_DIA = 1_440;
const DIAS_PADRAO = 30;
const DIAS_MAXIMO = 365;
const DIAS_SEM_EVENTO = 3;
const PRECO_PADRAO_CENTS = 24_900;
/** Mes de tarifa: e o divisor de tudo que e cobrado "por mes". */
const DIAS_NO_MES_DE_TARIFA = 30;

/** Minutos cobrados por ligacao: a Vapi arredonda para cima por chamada. */
const MINUTOS_LIGACAO_ATENDIDA = 2;
const MINUTOS_LIGACAO_NAO_ATENDIDA = 1;

/** Tarifas de EXEMPLO da previa. As reais vem das faturas (ver cabecalho, item 5). */
export const TARIFAS_DE_EXEMPLO: CostTariffs = {
  whatsappOfficialPerConversationCents: 35,
  whatsappEvolutionMonthlyPerInstanceCents: 8_900,
  emailPerThousandCents: 290,
  voicePerMinuteCents: 120,
  infraServerMonthlyCents: 38_000,
  infraDatabaseMonthlyCents: 26_000,
  source: 'exemplo',
};

type Cenario = 'saudavel' | 'whatsapp_desconectado' | 'integracao_muda' | 'sem_webhook';

const CICLO_DE_CENARIOS: readonly Cenario[] = ['saudavel', 'whatsapp_desconectado', 'integracao_muda'];

/** Hash pequeno e estavel do id: mesmo id, mesmo numero, em qualquer maquina. */
export function hashSimulado(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) % 10_007;
  }
  return h;
}

/** Dias cobertos pela janela, com teto; janela nula (desde o inicio) vale 30. */
export function diasDaJanela(range: PeriodRange): number {
  const dias = rangeDays(range);
  if (dias === null) {
    return DIAS_PADRAO;
  }
  return Math.max(1, Math.min(DIAS_MAXIMO, dias));
}

/** `base * bps / 10000`, arredondado para inteiro. Substitui qualquer `* 0.21`. */
function proporcao(base: number, bps: number): number {
  return Math.round((base * bps) / 10_000);
}

/**
 * Soma o valor de `quantidade` vendas repartidas entre as ofertas em
 * round-robin. `deslocamento` gira quem leva a venda extra, para o ticket
 * medio variar de um dia para o outro sem sair dos precos do catalogo.
 */
function distribuirVendas(ofertas: readonly ProductOffer[], quantidade: number, deslocamento: number): number {
  if (quantidade <= 0) {
    return 0;
  }
  if (ofertas.length === 0) {
    return quantidade * PRECO_PADRAO_CENTS;
  }
  const base = Math.floor(quantidade / ofertas.length);
  const resto = quantidade % ofertas.length;
  return ofertas.reduce((acc, oferta, i) => acc + oferta.priceCents * (base + ((i + deslocamento) % ofertas.length < resto ? 1 : 0)), 0);
}

function cenarioDa(empresa: CompanySummary, posicao: number): Cenario {
  if (empresa.status === 'onboarding') {
    return 'sem_webhook';
  }
  return CICLO_DE_CENARIOS[posicao % CICLO_DE_CENARIOS.length] ?? 'saudavel';
}

/**
 * Rota do WhatsApp pela posicao: impares na Evolution, pares na API oficial.
 * A empresa do cenario "whatsapp_desconectado" (posicao 1) precisa estar na
 * Evolution, porque o alerta fala da sessao do aparelho -- a API oficial nao
 * tem QR code para cair.
 */
function rotaDoWhatsApp(posicao: number): WhatsAppRoute {
  return posicao % 2 === 1 ? 'evolution' : 'meta_oficial';
}

/** Abandonos esperados por dia util; e tambem o peso da empresa no rateio da infra. */
function volumeDiario(empresa: CompanySummary, hash: number): number {
  return 8 + (hash % 9) + (empresa.funnels.includes('expirados') ? 2 + (hash % 3) : 0);
}

function ultimoEvento(empresa: CompanySummary, cenario: Cenario, hash: number): CompanyRow['lastEvent'] {
  if (empresa.status === 'pausada') {
    return { at: menosMinutos(9 * MINUTOS_POR_DIA + (hash % 300)), kind: 'carrinho_abandonado' };
  }
  switch (cenario) {
    case 'saudavel':
      return { at: menosMinutos(4 + (hash % 40)), kind: 'venda_recuperada' };
    case 'whatsapp_desconectado':
      return { at: menosMinutos(12 + (hash % 30)), kind: 'carrinho_abandonado' };
    case 'integracao_muda':
      return { at: menosMinutos(DIAS_SEM_EVENTO * MINUTOS_POR_DIA + 37), kind: 'carrinho_abandonado' };
    case 'sem_webhook':
      return null;
  }
}

// -----------------------------------------------------------------------------
// Uso e custo por dia
// -----------------------------------------------------------------------------

const USO_VAZIO: ChannelUsage = { voiceCalls: 0, voiceAnswered: 0, voiceMinutes: 0, whatsappMessages: 0, whatsappConversations: 0, emailsSent: 0 };
const CUSTO_VAZIO: CostByChannel = { voiceCents: 0, whatsappCents: 0, emailCents: 0, infraCents: 0, totalCents: 0 };

/** Quanto cada canal trabalhou num dia com `abandonedCount` entradas. */
function usoDoDia(abandonedCount: number, cenario: Cenario): ChannelUsage {
  // 1,3 tentativas por lead (RF08 permite ate 2); 56% dos leads atendem.
  const voiceCalls = proporcao(abandonedCount, 13_000);
  const voiceAnswered = Math.min(voiceCalls, proporcao(abandonedCount, 5_600));
  const voiceMinutes = voiceAnswered * MINUTOS_LIGACAO_ATENDIDA + (voiceCalls - voiceAnswered) * MINUTOS_LIGACAO_NAO_ATENDIDA;
  // Tier 2 so fala com quem a voz nao converteu; com o WhatsApp caido, nada sai.
  const tier2 = cenario === 'whatsapp_desconectado' ? 0 : 1;
  const whatsappConversations = tier2 * proporcao(abandonedCount, 4_400);
  const whatsappMessages = tier2 * proporcao(abandonedCount, 6_100);
  // O e-mail sai junto de cada toque, inclusive como reserva do WhatsApp.
  const emailsSent = proporcao(abandonedCount, 9_200);
  return { voiceCalls, voiceAnswered, voiceMinutes, whatsappMessages, whatsappConversations, emailsSent };
}

/** Uso x tarifa. `fixosCobrados` liga os custos que existem mesmo sem volume. */
function custoDoDia(uso: ChannelUsage, rota: WhatsAppRoute, fixosCobrados: boolean, infraDiaCents: number, tarifas: CostTariffs): CostByChannel {
  const voiceCents = uso.voiceMinutes * tarifas.voicePerMinuteCents;
  const whatsappCents =
    rota === 'meta_oficial'
      ? (uso.whatsappConversations ?? 0) * tarifas.whatsappOfficialPerConversationCents
      : fixosCobrados
        ? Math.round(tarifas.whatsappEvolutionMonthlyPerInstanceCents / DIAS_NO_MES_DE_TARIFA)
        : 0;
  const emailCents = Math.round((uso.emailsSent * tarifas.emailPerThousandCents) / 1_000);
  const infraCents = fixosCobrados ? infraDiaCents : 0;
  return { voiceCents, whatsappCents, emailCents, infraCents, totalCents: voiceCents + whatsappCents + emailCents + infraCents };
}

function somarUso(a: ChannelUsage, b: ChannelUsage): ChannelUsage {
  return {
    voiceCalls: a.voiceCalls + b.voiceCalls,
    voiceAnswered: a.voiceAnswered + b.voiceAnswered,
    voiceMinutes: a.voiceMinutes + b.voiceMinutes,
    whatsappMessages: a.whatsappMessages + b.whatsappMessages,
    whatsappConversations: (a.whatsappConversations ?? 0) + (b.whatsappConversations ?? 0),
    emailsSent: a.emailsSent + b.emailsSent,
  };
}

function somarCusto(a: CostByChannel, b: CostByChannel): CostByChannel {
  return {
    voiceCents: a.voiceCents + b.voiceCents,
    whatsappCents: a.whatsappCents + b.whatsappCents,
    emailCents: a.emailCents + b.emailCents,
    infraCents: a.infraCents + b.infraCents,
    totalCents: a.totalCents + b.totalCents,
  };
}

function margemBps(commissionCents: number, marginCents: number): number | null {
  return commissionCents === 0 ? null : Math.round((marginCents * 10_000) / commissionCents);
}

// -----------------------------------------------------------------------------
// Uma empresa, uma janela
// -----------------------------------------------------------------------------

interface JanelaSimulada {
  readonly abandonedCount: number;
  readonly recoveredCount: number;
  readonly recoveredAmountCents: number;
  readonly commissionCents: number;
  readonly reversedCount: number;
  readonly reversedAmountCents: number;
  readonly usage: ChannelUsage;
  readonly costByChannel: CostByChannel;
  readonly daily: readonly PlatformDailyPoint[];
}

interface EmpresaSimulada {
  readonly empresa: CompanySummary;
  readonly cenario: Cenario;
  readonly hash: number;
  readonly row: CompanyRow;
  readonly daily: readonly PlatformDailyPoint[];
}

function pontoVazio(day: string): PlatformDailyPoint {
  return { day, abandonedCount: 0, recoveredCount: 0, recoveredAmountCents: 0, commissionCents: 0, costCents: 0 };
}

function simularJanela(empresa: CompanySummary, cenario: Cenario, hash: number, rota: WhatsAppRoute, infraDiaCents: number, inicio: Date, dias: number): JanelaSimulada {
  const ofertas = ofertasDaEmpresa(empresa.id).filter((o) => o.active);
  const porDia = volumeDiario(empresa, hash);
  // Taxa de recuperacao entre 17% e 23%; sem Tier 2, cai 4 pontos.
  const taxaBps = 1_700 + (hash % 7) * 100 - (cenario === 'whatsapp_desconectado' ? 400 : 0);
  const limiteMudo = AGORA_SIMULADO.getTime() - DIAS_SEM_EVENTO * DIA_MS;
  const criadaEm = new Date(empresa.createdAt).getTime();

  let reversedCount = 0;
  let reversedAmountCents = 0;
  let usage = USO_VAZIO;
  let costByChannel = CUSTO_VAZIO;
  const daily: PlatformDailyPoint[] = [];

  for (let i = 0; i < dias; i += 1) {
    const dia = new Date(inicio.getTime() + i * DIA_MS);
    const day = dia.toISOString().slice(0, 10);
    // Onda, ticket e estorno dependem do DIA DO CALENDARIO, nao da posicao na
    // janela: o mesmo dia mostra os mesmos numeros em qualquer filtro, e
    // "hoje" nunca sai identico a "ontem" so porque os dois sao o indice 0.
    const diaDoCalendario = Math.floor(dia.getTime() / DIA_MS);
    // A empresa so existe (e so paga fixo) a partir do dia do cadastro.
    const existe = dia.getTime() + DIA_MS > criadaEm;
    const fixosCobrados = existe && empresa.status === 'ativa';
    const parado = !fixosCobrados || (cenario === 'integracao_muda' && dia.getTime() >= limiteMudo);
    const uso = parado ? USO_VAZIO : usoDoDia(volumeDoDia(porDia, diaDoCalendario, hash, dia), cenario);
    const custo = custoDoDia(uso, rota, fixosCobrados, infraDiaCents, TARIFAS_DE_EXEMPLO);
    usage = somarUso(usage, uso);
    costByChannel = somarCusto(costByChannel, custo);

    if (parado) {
      daily.push({ ...pontoVazio(day), costCents: custo.totalCents });
      continue;
    }
    const abandonedCount = volumeDoDia(porDia, diaDoCalendario, hash, dia);
    const recoveredCount = proporcao(abandonedCount, taxaBps);
    const recoveredAmountCents = distribuirVendas(ofertas, recoveredCount, diaDoCalendario);
    const commissionCents = proporcao(recoveredAmountCents, empresa.feeBps);
    // Um estorno a cada ~19 dias com venda: raro, mas presente em qualquer janela de um mes.
    if (recoveredCount > 0 && (diaDoCalendario + hash) % 19 === 0) {
      reversedCount += 1;
      reversedAmountCents += ofertaCiclica(empresa.id, i).priceCents;
    }
    daily.push({ day, abandonedCount, recoveredCount, recoveredAmountCents, commissionCents, costCents: custo.totalCents });
  }

  const soma = daily.reduce(
    (acc, p) => ({
      abandonados: acc.abandonados + p.abandonedCount,
      recuperados: acc.recuperados + p.recoveredCount,
      bruto: acc.bruto + p.recoveredAmountCents,
      comissao: acc.comissao + p.commissionCents,
    }),
    { abandonados: 0, recuperados: 0, bruto: 0, comissao: 0 },
  );

  return {
    abandonedCount: soma.abandonados,
    recoveredCount: soma.recuperados,
    recoveredAmountCents: soma.bruto,
    commissionCents: soma.comissao,
    reversedCount,
    reversedAmountCents,
    usage,
    costByChannel,
    daily,
  };
}

/** Onda deterministica pelo dia do calendario: fim de semana a 55%, dias uteis entre 80% e 120%. */
function volumeDoDia(porDia: number, diaDoCalendario: number, hash: number, dia: Date): number {
  const diaDaSemana = dia.getUTCDay();
  const fimDeSemana = diaDaSemana === 0 || diaDaSemana === 6;
  const ondaPct = fimDeSemana ? 55 : 80 + ((diaDoCalendario * 7 + hash) % 9) * 5;
  return Math.max(1, Math.round((porDia * ondaPct) / 100));
}

function simularEmpresa(empresa: CompanySummary, posicao: number, infraDiaCents: number, inicio: Date, dias: number, inicioAnterior: Date | null): EmpresaSimulada {
  const hash = hashSimulado(empresa.id);
  const cenario = cenarioDa(empresa, posicao);
  const rota = rotaDoWhatsApp(posicao);
  const atual = simularJanela(empresa, cenario, hash, rota, infraDiaCents, inicio, dias);
  const anterior = inicioAnterior === null ? null : simularJanela(empresa, cenario, hash, rota, infraDiaCents, inicioAnterior, dias);

  const marginCents = atual.commissionCents - atual.costByChannel.totalCents;
  const previous: CompanyPrevious | null =
    anterior === null
      ? null
      : {
          abandonedCount: anterior.abandonedCount,
          recoveredCount: anterior.recoveredCount,
          recoveredAmountCents: anterior.recoveredAmountCents,
          commissionCents: anterior.commissionCents,
          costCents: anterior.costByChannel.totalCents,
          marginCents: anterior.commissionCents - anterior.costByChannel.totalCents,
        };

  const row: CompanyRow = {
    companyId: empresa.id,
    companyName: empresa.name,
    status: empresa.status,
    billingModel: empresa.billingModel,
    feeBps: empresa.feeBps,
    activeFunnels: empresa.status === 'ativa' ? empresa.funnels : [],
    abandonedCount: atual.abandonedCount,
    recoveredCount: atual.recoveredCount,
    recoveryRateBps: atual.abandonedCount === 0 ? 0 : Math.round((atual.recoveredCount * 10_000) / atual.abandonedCount),
    recoveredAmountCents: atual.recoveredAmountCents,
    commissionCents: atual.commissionCents,
    netAmountCents: atual.recoveredAmountCents - atual.commissionCents,
    reversedCount: atual.reversedCount,
    reversedAmountCents: atual.reversedAmountCents,
    lastEvent: ultimoEvento(empresa, cenario, hash),
    whatsappConnected: empresa.status === 'ativa' && cenario !== 'whatsapp_desconectado',
    webhookConfigured: cenario !== 'sem_webhook',
    createdAt: empresa.createdAt,
    whatsappRoute: rota,
    usage: atual.usage,
    costByChannel: atual.costByChannel,
    costCents: atual.costByChannel.totalCents,
    marginCents,
    marginBps: margemBps(atual.commissionCents, marginCents),
    previous,
  };

  return { empresa, cenario, hash, row, daily: atual.daily };
}

// -----------------------------------------------------------------------------
// Alertas (retrato de agora; margem negativa e retrato do periodo)
// -----------------------------------------------------------------------------

/** Minutos atras do estorno de exemplo; o mesmo instante aparece na atividade recente. */
const MINUTOS_DO_ESTORNO = 58;
const INDICE_DA_OFERTA_ESTORNADA = 3;

const ORDEM_GRAVIDADE: Readonly<Record<AlertSeverity, number>> = { critical: 0, warning: 1, info: 2 };

function alertaDoCenario(sim: EmpresaSimulada): PlatformAlert | null {
  const { empresa, cenario, hash } = sim;
  const provedor = PROVIDER_LABEL[empresa.providers[0] ?? 'kiwify'];
  switch (cenario) {
    case 'whatsapp_desconectado':
      return {
        id: `SIMULADO-alerta-${empresa.slug}-whatsapp`,
        companyId: empresa.id,
        kind: 'whatsapp_desconectado',
        severity: 'critical',
        detail: 'A instância da Evolution API perdeu a sessão do aparelho. O Tier 2 (WhatsApp) não envia nada até reconectar pelo QR code, e a instância continua sendo cobrada.',
        amountCents: null,
        occurredAt: menosMinutos(95 + (hash % 20)),
      };
    case 'integracao_muda':
      return {
        id: `SIMULADO-alerta-${empresa.slug}-integracao`,
        companyId: empresa.id,
        kind: 'integracao_sem_evento',
        severity: 'warning',
        detail: `Nenhum webhook da ${provedor} chegou nos últimos ${DIAS_SEM_EVENTO} dias. Sem abandono chegando não há o que recuperar; confira URL e segredo no painel do provedor.`,
        amountCents: null,
        occurredAt: sim.row.lastEvent?.at ?? menosMinutos(DIAS_SEM_EVENTO * MINUTOS_POR_DIA),
      };
    case 'sem_webhook':
      return {
        id: `SIMULADO-alerta-${empresa.slug}-webhook`,
        companyId: empresa.id,
        kind: 'implantacao_sem_webhook',
        severity: 'info',
        detail: `Empresa em implantação sem nenhuma URL de webhook configurada na ${provedor}. Até isso acontecer, nenhum carrinho chega ao motor e não há comissão.`,
        amountCents: null,
        occurredAt: empresa.createdAt,
      };
    case 'saudavel': {
      if (empresa.status !== 'ativa') {
        return null;
      }
      const oferta = ofertaCiclica(empresa.id, INDICE_DA_OFERTA_ESTORNADA);
      return {
        id: `SIMULADO-alerta-${empresa.slug}-chargeback`,
        companyId: empresa.id,
        kind: 'chargeback_recebido',
        severity: 'warning',
        detail: `Chargeback em ${oferta.name}. A conversão foi revertida e a comissão saiu da apuração do período (política da empresa: reverte chargeback).`,
        amountCents: oferta.priceCents,
        occurredAt: menosMinutos(MINUTOS_DO_ESTORNO),
      };
    }
  }
}

function alertaDeMargem(sim: EmpresaSimulada): PlatformAlert | null {
  const { empresa, row } = sim;
  if (empresa.status !== 'ativa' || row.marginCents === null || row.marginCents >= 0) {
    return null;
  }
  return {
    id: `SIMULADO-alerta-${empresa.slug}-margem`,
    companyId: empresa.id,
    kind: 'margem_negativa',
    severity: 'warning',
    detail: `Custos de ${formatBRLFromCents(row.costCents ?? 0)} contra ${formatBRLFromCents(row.commissionCents)} de comissão no período (WhatsApp pela ${WHATSAPP_ROUTE_LABEL[row.whatsappRoute]}). Confira volume e tarifa antes de faturar.`,
    amountCents: -row.marginCents,
    occurredAt: AGORA_SIMULADO.toISOString(),
  };
}

function ordenarAlertas(alertas: readonly PlatformAlert[]): readonly PlatformAlert[] {
  return [...alertas].sort((a, b) => {
    const porGravidade = ORDEM_GRAVIDADE[a.severity] - ORDEM_GRAVIDADE[b.severity];
    if (porGravidade !== 0) {
      return porGravidade;
    }
    return a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0;
  });
}

// -----------------------------------------------------------------------------
// Atividade recente (retrato de agora)
// -----------------------------------------------------------------------------

interface ModeloDeEvento {
  readonly kind: PlatformEventKind;
  /** Posicao da empresa no catalogo; fora da lista cai na primeira. */
  readonly empresa: number;
  readonly minutosAtras: number;
  readonly lead: string | null;
  /** Indice da oferta (ciclico) ou `null` quando o evento nao tem produto. */
  readonly oferta: number | null;
}

/**
 * Lista fixa, como as de lib/api.ts. A empresa 0 e a "saudavel" e a 1 e a
 * do WhatsApp desconectado (ver `cenarioDa`), por isso a 1 nao tem evento de
 * WhatsApp aqui. O estorno da empresa 0 e o mesmo do alerta de chargeback.
 */
const MODELOS_DE_EVENTOS: readonly ModeloDeEvento[] = [
  { kind: 'venda_recuperada', empresa: 0, minutosAtras: 6, lead: 'Aline Souza Prado', oferta: 5 },
  { kind: 'ligacao_ia', empresa: 1, minutosAtras: 14, lead: 'Marcos Vinícius Rocha', oferta: 1 },
  { kind: 'carrinho_abandonado', empresa: 0, minutosAtras: 21, lead: 'Mariana Ferraz', oferta: 0 },
  { kind: 'whatsapp_enviado', empresa: 0, minutosAtras: 33, lead: 'João Batista Lemos', oferta: 1 },
  { kind: 'email_enviado', empresa: 0, minutosAtras: 34, lead: 'João Batista Lemos', oferta: 1 },
  { kind: 'estorno', empresa: 0, minutosAtras: MINUTOS_DO_ESTORNO, lead: 'Ricardo Nunes', oferta: INDICE_DA_OFERTA_ESTORNADA },
  { kind: 'carrinho_abandonado', empresa: 1, minutosAtras: 72, lead: 'Beatriz Camargo', oferta: 0 },
  { kind: 'webhook_recebido', empresa: 1, minutosAtras: 73, lead: null, oferta: null },
  { kind: 'venda_recuperada', empresa: 1, minutosAtras: 140, lead: 'Fernanda Lopes', oferta: 2 },
  { kind: 'ligacao_ia', empresa: 0, minutosAtras: 185, lead: 'Carlos Eduardo Pires', oferta: 2 },
  { kind: 'email_enviado', empresa: 1, minutosAtras: 190, lead: 'Fernanda Lopes', oferta: 2 },
  { kind: 'carrinho_abandonado', empresa: 2, minutosAtras: 4_357, lead: 'Helena Barros', oferta: 1 },
  { kind: 'empresa_cadastrada', empresa: 3, minutosAtras: 0, lead: null, oferta: null },
];

/** Eventos que carregam o valor do carrinho; ligacao e mensagem nao repetem o numero. */
const EVENTO_TEM_VALOR: Readonly<Record<PlatformEventKind, boolean>> = {
  carrinho_abandonado: true,
  ligacao_ia: false,
  whatsapp_enviado: false,
  email_enviado: false,
  venda_recuperada: true,
  estorno: true,
  webhook_recebido: false,
  empresa_cadastrada: false,
};

function eventosRecentes(): readonly PlatformEvent[] {
  const primeira = EMPRESAS[0];
  if (primeira === undefined) {
    return [];
  }
  const eventos = MODELOS_DE_EVENTOS.map((m, i): PlatformEvent => {
    const empresa = EMPRESAS[m.empresa] ?? primeira;
    const oferta = m.oferta === null ? null : ofertaCiclica(empresa.id, m.oferta);
    return {
      id: `SIMULADO-evento-${String(i + 1).padStart(3, '0')}`,
      companyId: empresa.id,
      kind: m.kind,
      leadName: m.lead,
      offerName: oferta === null ? null : oferta.name,
      amountCents: oferta !== null && EVENTO_TEM_VALOR[m.kind] ? oferta.priceCents : null,
      occurredAt: m.kind === 'empresa_cadastrada' ? empresa.createdAt : menosMinutos(m.minutosAtras),
    };
  });
  return eventos.sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0));
}

// -----------------------------------------------------------------------------
// Consolidacao
// -----------------------------------------------------------------------------

/** Soma as series por indice: todas partem do mesmo `inicio` com os mesmos `dias`. */
function somarSeries(series: readonly (readonly PlatformDailyPoint[])[], inicio: Date, dias: number): readonly PlatformDailyPoint[] {
  return Array.from({ length: dias }, (_, i) => {
    const day = new Date(inicio.getTime() + i * DIA_MS).toISOString().slice(0, 10);
    return series.reduce<PlatformDailyPoint>((acc, serie) => {
      const p = serie[i];
      if (p === undefined) {
        return acc;
      }
      return {
        day,
        abandonedCount: acc.abandonedCount + p.abandonedCount,
        recoveredCount: acc.recoveredCount + p.recoveredCount,
        recoveredAmountCents: acc.recoveredAmountCents + p.recoveredAmountCents,
        commissionCents: acc.commissionCents + p.commissionCents,
        costCents: (acc.costCents ?? 0) + (p.costCents ?? 0),
      };
    }, pontoVazio(day));
  });
}

interface FiguraDaJanela {
  readonly abandonedCount: number;
  readonly recoveredCount: number;
  readonly recoveredAmountCents: number;
  readonly commissionCents: number;
  readonly reversedCount: number;
  readonly reversedAmountCents: number;
  readonly usage: ChannelUsage;
  /** Na previa o custo sempre existe; `null` so para aceitar a linha tipada da API. */
  readonly costByChannel: CostByChannel | null;
}

function fecharTotais(figuras: readonly FiguraDaJanela[]): PlatformTotals {
  const soma = figuras.reduce(
    (acc, r) => ({
      bruto: acc.bruto + r.recoveredAmountCents,
      comissao: acc.comissao + r.commissionCents,
      abandonados: acc.abandonados + r.abandonedCount,
      recuperados: acc.recuperados + r.recoveredCount,
      estornos: acc.estornos + r.reversedCount,
      estornado: acc.estornado + r.reversedAmountCents,
      usage: somarUso(acc.usage, r.usage),
      custo: somarCusto(acc.custo, r.costByChannel ?? CUSTO_VAZIO),
    }),
    { bruto: 0, comissao: 0, abandonados: 0, recuperados: 0, estornos: 0, estornado: 0, usage: USO_VAZIO, custo: CUSTO_VAZIO },
  );
  const marginCents = soma.comissao - soma.custo.totalCents;
  return {
    recoveredAmountCents: soma.bruto,
    commissionCents: soma.comissao,
    netAmountCents: soma.bruto - soma.comissao,
    abandonedCount: soma.abandonados,
    recoveredCount: soma.recuperados,
    recoveryRateBps: soma.abandonados === 0 ? 0 : Math.round((soma.recuperados * 10_000) / soma.abandonados),
    reversedCount: soma.estornos,
    reversedAmountCents: soma.estornado,
    costCents: soma.custo.totalCents,
    costByChannel: soma.custo,
    marginCents,
    marginBps: margemBps(soma.comissao, marginCents),
    usage: soma.usage,
  };
}

function contarStatus(empresas: readonly CompanySummary[]): Readonly<Record<CompanyStatus, number>> {
  return empresas.reduce<Record<CompanyStatus, number>>(
    (acc, e) => ({ ...acc, [e.status]: acc[e.status] + 1 }),
    { ativa: 0, onboarding: 0, pausada: 0 },
  );
}

/** Fim exclusivo da janela; sem filtro, a proxima meia-noite UTC depois de AGORA_SIMULADO, para "hoje" entrar. */
function fimDaJanela(range: PeriodRange): Date {
  if (range.to !== null) {
    return new Date(range.to);
  }
  return new Date(Date.UTC(AGORA_SIMULADO.getUTCFullYear(), AGORA_SIMULADO.getUTCMonth(), AGORA_SIMULADO.getUTCDate() + 1));
}

/**
 * Rateio da infra: o custo diario (servidor + banco / 30) dividido entre as
 * empresas ATIVAS na proporcao do volume diario esperado. Empresas em
 * implantacao ou pausadas nao pagam infra: ainda nao usam o motor.
 */
function infraPorEmpresa(empresas: readonly CompanySummary[], tarifas: CostTariffs): ReadonlyMap<string, number> {
  const infraDia = Math.round((tarifas.infraServerMonthlyCents + tarifas.infraDatabaseMonthlyCents) / DIAS_NO_MES_DE_TARIFA);
  const ativas = empresas.filter((e) => e.status === 'ativa');
  const pesos = ativas.map((e) => volumeDiario(e, hashSimulado(e.id)));
  const somaPesos = pesos.reduce((acc, p) => acc + p, 0);
  const mapa = new Map<string, number>();
  ativas.forEach((e, i) => {
    const peso = pesos[i] ?? 0;
    mapa.set(e.id, somaPesos === 0 ? 0 : Math.round((infraDia * peso) / somaPesos));
  });
  return mapa;
}

/**
 * Retrato da janela anterior de uma empresa como figura somavel. A janela
 * anterior nao detalha uso nem canal (so o total de custo importa para a
 * comparacao), entao os campos por canal ficam zerados de proposito.
 */
function figuraAnterior(previous: CompanyPrevious): FiguraDaJanela {
  return {
    abandonedCount: previous.abandonedCount,
    recoveredCount: previous.recoveredCount,
    recoveredAmountCents: previous.recoveredAmountCents,
    commissionCents: previous.commissionCents,
    reversedCount: 0,
    reversedAmountCents: 0,
    usage: USO_VAZIO,
    costByChannel: { ...CUSTO_VAZIO, totalCents: previous.costCents ?? 0 },
  };
}

export function simularCentral(range: PeriodRange): PlatformOverview {
  const dias = diasDaJanela(range);
  const fim = fimDaJanela(range);
  const inicio = range.from === null ? new Date(fim.getTime() - dias * DIA_MS) : new Date(range.from);
  const temAnterior = range.from !== null;
  const inicioAnterior = temAnterior ? new Date(inicio.getTime() - dias * DIA_MS) : null;
  const infra = infraPorEmpresa(EMPRESAS, TARIFAS_DE_EXEMPLO);

  const simuladas = EMPRESAS.map((empresa, posicao) => simularEmpresa(empresa, posicao, infra.get(empresa.id) ?? 0, inicio, dias, inicioAnterior));
  const companies = simuladas.map((s) => s.row).sort((a, b) => b.recoveredAmountCents - a.recoveredAmountCents);
  const alerts = ordenarAlertas([...simuladas.map(alertaDoCenario), ...simuladas.map(alertaDeMargem)].filter((a): a is PlatformAlert => a !== null));

  const previousTotals = temAnterior
    ? fecharTotais(companies.map((row) => row.previous).filter((p): p is CompanyPrevious => p !== null).map(figuraAnterior))
    : null;

  const novas = EMPRESAS.filter((e) => {
    const criada = new Date(e.createdAt).getTime();
    return criada >= inicio.getTime() && criada < fim.getTime();
  }).length;

  return {
    totals: fecharTotais(companies),
    previousTotals,
    previousPeriod: inicioAnterior === null ? null : { start: inicioAnterior.toISOString(), end: inicio.toISOString() },
    statusCounts: contarStatus(EMPRESAS),
    newCompaniesCount: novas,
    companies,
    daily: somarSeries(simuladas.map((s) => s.daily), inicio, dias),
    alerts,
    recentEvents: eventosRecentes(),
    tariffs: TARIFAS_DE_EXEMPLO,
    // A previa tem tarifas de exemplo: os custos existem (simulados).
    costsAvailable: true,
    capturedAt: AGORA_SIMULADO.toISOString(),
    periodStart: inicio.toISOString(),
    periodEnd: fim.toISOString(),
  };
}
